import { useEffect, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Add } from '@mesh.ai/rabbit';
import { L3_PATHS } from 'Deer/constants/routes';
import { useDeerTeamType } from 'Deer/hooks/useDeerTeamType';
import { useQuery } from 'components/hooks/useQuery';
import { stringify } from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { closeSidebar } from 'reducers/sidebarNav-slice';
import { useSelectedView } from 'reducers/viewsSlice';
import { OneOnOneCompletion } from 'screens/oneOnOneRabbit/Insights/features/Graphs/OneOnOneCompletion/OneOnOneCompletion';
import ThemeDistribution from 'screens/oneOnOneRabbit/Insights/features/Graphs/ThemeDistribution/ThemeDistribution';
import OneOnOneHeading from 'screens/oneOnOneRabbit/Insights/features/OneOnOneHeading';
import {
  setFilterTextOneOnOneInsightsFilters,
  useOneOnOneInsightsFiltersData,
} from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';
import { openCreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer/slice/CreateOneOnOneDrawerSlice';
import ActiveOneOnOneTable from 'screens/oneOnOneRabbit/landingPage/features/LandingPageTable/ActiveOneOnOneTable';
import ArchivedOneOnOneTable from 'screens/oneOnOneRabbit/landingPage/features/LandingPageTable/ArchivedOneOnOneTable';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { checkEnableNetchexEmployeeNavbar, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Plus, Search } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';
import { sendClickedAddNewOnOneOnOne, sendClickedOnOneOnOneFilter, sendPageViewEvent } from 'constants/mixpanelHelper';

import { AgGridSearch } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridActionButton } from 'uiToolkitV1/AgGridWrapperV2/components/ActionButton/AgGridActionButton';
import { Button } from 'uiToolkitV1/Button';
import { ChakraStyleInput } from 'uiToolkitV1/InputV2/ChakraStyleInput';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

const ONE_ON_ONE_FILTERS_ARRAY = [
  { sectionId: 'ACTIVE', sectionKeyName: 'Active', beKey: 'activeCount' },
  { sectionId: 'ARCHIVED', sectionKeyName: 'Archived', beKey: 'archivedCount' },
];

const TABLE_TYPES_COMP = {
  ACTIVE: ActiveOneOnOneTable,
  ARCHIVED: ArchivedOneOnOneTable,
};

const getPageForMixpanel = ({
  isSkipTeam,
  oneOnOneType,
}: {
  isSkipTeam: boolean;
  oneOnOneType: 'ACTIVE' | 'ARCHIVED';
}) => {
  if (isSkipTeam) {
    if (oneOnOneType === 'ACTIVE') {
      return 'Skip team-1:1-manage';
    }
    return 'Skip team-1:1-archived';
  }
  if (oneOnOneType === 'ACTIVE') {
    return 'Direct team-1:1-manage';
  }
  return 'Direct team-1:1-archived';
};

const OneOnOneManageIntent = () => {
  const { oneOnOneType } = useQuery();
  const history = useHistory();
  const location = useLocation();
  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);
  const employeeId = loggedInEmployeeData?.id;
  const selectedCohort = useSelectedView();
  const { dateRange, filterText } = useOneOnOneInsightsFiltersData();
  const teamType = useDeerTeamType();
  const isSkipTeam = teamType === 'allReports';

  const dispatch = useDispatch();

  const gridRef = useRef(null);

  // eslint-disable-next-line no-shadow
  const getRouteHelper = ({ oneOnOneType }) => {
    const pathname = location?.pathname ?? '';
    const isDirectReports = pathname.includes('/direct-reports/');
    const stringifiedOneOnOneType = stringify({ oneOnOneType });
    // Determine the pathname based on the condition
    const newSearch = `&${stringifiedOneOnOneType}`;
    let newPathname = '';

    if (isDirectReports) {
      newPathname = L3_PATHS.team.directReports.oneOnOne.manage;
    } else {
      newPathname = L3_PATHS.team.allReports.oneOnOne.manage;
    }

    return {
      pathname: newPathname,
      search: newSearch,
    };
  };

  const filterChangeHandler = (newSelectedTableId) => {
    sendClickedOnOneOnOneFilter({
      eventProperties: {
        type: newSelectedTableId[0],
      },
    });
    history.replace(
      getRouteHelper({
        oneOnOneType: newSelectedTableId[0],
      })
    );
  };

  const onTextFilterChangeHandler = ({ value }) => {
    dispatch(
      setFilterTextOneOnOneInsightsFilters({
        filterText: value,
      })
    );
  };

  const openCreateOneOnOneSidepanel = () => {
    sendClickedAddNewOnOneOnOne();
    dispatch(
      // @ts-expect-error not properly typed yet
      openCreateOneOnOneDrawer({
        excludedParticipantIds: [],
      })
    );
  };

  useEffect(() => {
    if (!oneOnOneType) {
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('oneOnOneType', ONE_ON_ONE_FILTERS_ARRAY[0].sectionId);
      history.replace({
        search: queryParams.toString(),
      });
    }
  }, [history, location.search, oneOnOneType]);

  useEffect(() => {
    if (oneOnOneType === 'ACTIVE' || oneOnOneType === 'ARCHIVED') {
      sendPageViewEvent({
        eventProperties: {
          page: getPageForMixpanel({ isSkipTeam, oneOnOneType }),
        },
      });
    }
  }, [oneOnOneType, isSkipTeam]);

  useEffect(() => {
    if (isMobileView()) {
      dispatch(closeSidebar());
    }
  }, [dispatch]);

  const TableComp = TABLE_TYPES_COMP?.[oneOnOneType] ?? (() => null);
  const isEnableNetchexEmployeeNavbar = checkEnableNetchexEmployeeNavbar();

  if (isMobileView()) {
    return (
      <>
        {!isEnableNetchexEmployeeNavbar && <OneOnOneHeading />}

        <PaperWithShadow p="10px 12px">
          <Flex alignItems="center">
            <Box w="100%">
              <Flex>
                <ChakraStyleInput
                  leftComponent={<Search />}
                  placeholder="Search"
                  value={filterText}
                  onChange={(e) => onTextFilterChangeHandler({ value: e.target.value })}
                  flex={1}
                />
                <Button ml="5px" StartIcon={Plus} onClick={openCreateOneOnOneSidepanel}>
                  Add New
                </Button>
              </Flex>
            </Box>
          </Flex>

          <TableComp />
        </PaperWithShadow>
      </>
    );
  }

  return (
    <>
      {!checkNewNavEnabled() && <OneOnOneHeading />}
      <Flex mb={3} gap={3} h="348px">
        <PaperWithShadow w="50%" p={0} h="100%">
          <OneOnOneCompletion isManagePage />
        </PaperWithShadow>
        <PaperWithShadow w="50%" p={0} h="100%">
          <ThemeDistribution />
        </PaperWithShadow>
      </Flex>

      <PaperWithShadow p="12px">
        <Flex justifyContent="space-between" alignItems="center">
          <Box>
            <Box mr="auto">
              <AgGridSearch gridRef={gridRef} placeholder="Search" cohortId={oneOnOneType} />
            </Box>
          </Box>

          <AgGridActionButton Icon={Add} onClick={openCreateOneOnOneSidepanel} label="Add New" />
        </Flex>

        <TableComp gridRef={gridRef} />
      </PaperWithShadow>
    </>
  );
};

export { OneOnOneManageIntent };
